import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const DEMO = [
  { label: "IT Admin", email: "admin@vaultvec.io" },
  { label: "Compliance", email: "compliance@vaultvec.io" },
  { label: "Auditor", email: "auditor@vaultvec.io" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("error") === "google") setError("Google sign-in failed. Please try again.");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (mail) => {
    setEmail(mail);
    setPassword("Vaultvec#2026");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative border-r border-border grid-texture">
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="h-7 w-7 bg-primary flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-white" /></div>
            <span className="font-heading text-lg font-extrabold tracking-tighter">VAULTVEC</span>
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tighter leading-tight">Secure command center for pharmaceutical cyber protection.</h2>
            <p className="mt-4 text-muted-foreground font-mono text-xs tracking-widest">FDA 21 CFR PART 11 · GxP · GAMP 5</p>
          </div>
          <div />
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-2">Access the Vaultvec protection console.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none bg-secondary/40 border-border mt-1.5" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Password</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none bg-secondary/40 border-border mt-1.5" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-primary font-mono">{error}</p>}
            <Button data-testid="login-submit" disabled={busy} className="w-full rounded-none bg-primary hover:bg-primary/90 text-white h-11">
              {busy ? "Authenticating…" : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] tracking-widest text-muted-foreground">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full h-11 flex items-center justify-center gap-3 border border-border bg-secondary/40 hover:bg-secondary transition-colors text-sm"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>

          <div className="mt-6 border border-border p-4">
            <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-3">DEMO ACCOUNTS · pw Vaultvec#2026</p>
            <div className="flex flex-wrap gap-2">
              {DEMO.map((d) => (
                <button key={d.email} type="button" data-testid={`demo-${d.label.toLowerCase()}`} onClick={() => fillDemo(d.email)} className="text-xs border border-border px-3 py-1.5 hover:bg-secondary transition-colors">
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-6">
            No account? <Link to="/register" className="text-primary hover:underline">Create one</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
